import { createRouter, createWebHistory } from 'vue-router';
import { ADMIN_ROUTE_MODULE_MAP } from '../admin/config/adminMenu';
import { useAuthStore } from '../stores/auth';
import { useFrontCmsStore } from '../stores/frontCms';
import HomePage from '../pages/HomePage.vue';
import CloudGamingPage from '../pages/CloudGamingPage.vue';
import ComputingPowerPage from '../pages/ComputingPowerPage.vue';
import AiAccelerationPage from '../pages/AiAccelerationPage.vue';
import AboutHaixiPage from '../pages/AboutHaixiPage.vue';
import ProductsPage from '../pages/ProductsPage.vue';
import SolutionsPage from '../pages/SolutionsPage.vue';
import TechEcosystemPage from '../pages/TechEcosystemPage.vue';
import CasesPage from '../pages/CasesPage.vue';
import NewsPage from '../pages/NewsPage.vue';
import NewsDetailPage from '../pages/NewsDetailPage.vue';
import CareersPage from '../pages/CareersPage.vue';
import ContactPage from '../pages/ContactPage.vue';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    { path: '/', name: 'home', component: HomePage },
    { path: '/cloud-gaming', name: 'cloud-gaming', component: CloudGamingPage },
    { path: '/computing-power', name: 'computing-power', component: ComputingPowerPage },
    { path: '/ai-acceleration', name: 'ai-acceleration', component: AiAccelerationPage },
    { path: '/about-haixi', name: 'about-haixi', component: AboutHaixiPage },
    {
      path: '/products',
      name: 'products',
      component: ProductsPage,
      beforeEnter: () => {
        const cms = useFrontCmsStore();
        cms.hydrateFromCache();
        void cms.ensure('productCenter');
        return true;
      }
    },
    { path: '/solutions', name: 'solutions', component: SolutionsPage },
    { path: '/tech-ecosystem', name: 'tech-ecosystem', component: TechEcosystemPage },
    { path: '/cases', name: 'cases', component: CasesPage },
    { path: '/news', name: 'news', component: NewsPage },
    { path: '/news/:id', name: 'news-detail', component: NewsDetailPage },
    { path: '/careers', name: 'careers', component: CareersPage },
    { path: '/contact', name: 'contact', component: ContactPage },
    {
      path: '/admin/login',
      name: 'admin-login',
      component: () => import('../admin/views/AdminLoginView.vue'),
      meta: { admin: true, guestOnly: true }
    },
    {
      path: '/admin',
      component: () => import('../admin/layouts/AdminLayout.vue'),
      meta: { admin: true, requiresAuth: true },
      children: [
        { path: '', redirect: '/admin/dashboard' },
        {
          path: 'dashboard',
          name: 'admin-dashboard',
          component: () => import('../admin/views/AdminDashboardView.vue')
        },
        {
          path: 'routes',
          name: 'admin-routes',
          meta: { moduleCode: 'cms:route' },
          component: () => import('../admin/pages/RoutesManagement.vue')
        },
        {
          path: 'home',
          name: 'admin-home',
          meta: { moduleCode: 'cms:homepage' },
          component: () => import('../admin/pages/HomeManagement.vue')
        },
        {
          path: 'products',
          name: 'admin-products',
          meta: { moduleCode: 'cms:product' },
          component: () => import('../admin/pages/ProductsManagement.vue')
        },
        {
          path: 'solutions',
          name: 'admin-solutions',
          meta: { moduleCode: 'cms:solution' },
          component: () => import('../admin/pages/SolutionsManagement.vue')
        },
        {
          path: 'cases',
          name: 'admin-cases',
          meta: { moduleCode: 'cms:success-case' },
          component: () => import('../admin/pages/CasesManagement.vue')
        },
        {
          path: 'jobs',
          name: 'admin-jobs',
          meta: { moduleCode: 'cms:recruitment' },
          component: () => import('../admin/pages/JobsManagement.vue')
        },
        {
          path: 'messages',
          name: 'admin-messages',
          meta: { moduleCode: 'cms:guestbook' },
          component: () => import('../admin/pages/MessagesManagement.vue')
        },
        {
          path: 'news',
          name: 'admin-news',
          meta: { moduleCode: 'cms:news' },
          component: () => import('../admin/pages/NewsManagement.vue')
        },
        {
          path: 'users',
          name: 'admin-users',
          meta: { moduleCode: 'system:user' },
          component: () => import('../admin/pages/UsersManagement.vue')
        }
      ]
    },

  ],
  scrollBehavior(to) {
    if (to.path.startsWith('/admin')) {
      return { top: 0 };
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' };
    }
    // 路由切换回顶部：瞬时定位，不做平滑滚动动画
    return { top: 0 };
  }
});

router.beforeEach((to) => {
  const auth = useAuthStore();
  auth.hydrateFromStorage();

  if (!to.meta.admin) {
    return true;
  }

  if (to.meta.requiresAuth && !auth.isLoggedIn) {
    return {
      name: 'admin-login',
      query: to.fullPath !== '/admin' && to.fullPath !== '/admin/' ? { redirect: to.fullPath } : {}
    };
  }

  if (to.meta.guestOnly && auth.isLoggedIn) {
    return { name: 'admin-dashboard' };
  }

  const moduleCode = (to.meta.moduleCode as string | undefined) ?? ADMIN_ROUTE_MODULE_MAP[to.path];
  if (moduleCode && !auth.hasModule(moduleCode)) {
    return { name: 'admin-dashboard' };
  }

  return true;
});

export default router;
